import React, { useState, useRef, useEffect } from 'react';
import { Calendar as CalendarIcon, Trash2 } from 'lucide-react';
// @ts-ignore
import { Calendar } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

export interface ChildInfo {
  id?: number;
  fullName: string;
  birthDate: string;
  medicalConditions: string;
}

interface CheckoutChildInfoProps {
  childInfo: ChildInfo;
  onChange: (info: ChildInfo) => void;
  onBlur?: () => void;
  onRemove?: () => void;
  errors?: Partial<Record<keyof ChildInfo, string>>;
  index: number;
  canRemove?: boolean;
}

const CheckoutChildInfo: React.FC<CheckoutChildInfoProps> = ({
  childInfo,
  onChange,
  onBlur,
  onRemove,
  errors = {},
  index,
  canRemove = false,
}) => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [displayDate, setDisplayDate] = useState('');
  const pickerRef = useRef<HTMLDivElement>(null);

  // Convert YYYY-MM-DD to MM/DD/YYYY for display
  const formatToUSDate = (isoDate: string): string => {
    if (!isoDate) return '';
    const parts = isoDate.split('-');
    if (parts.length === 3) {
      return `${parts[1]}/${parts[2]}/${parts[0]}`;
    }
    return isoDate;
  };

  // Convert MM/DD/YYYY to YYYY-MM-DD for storage
  const parseUSDate = (usDate: string): string | null => {
    const match = usDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (match) {
      const [, month, day, year] = match;
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }
    }
    return null;
  };

  // Sync displayDate when childInfo.birthDate changes externally
  useEffect(() => {
    setDisplayDate(formatToUSDate(childInfo.birthDate));
  }, [childInfo.birthDate]);

  const handleChange = (field: keyof ChildInfo, value: string) => {
    onChange({
      ...childInfo,
      [field]: value,
    });
  };

  const handleDateChange = (date: Date) => {
    const formattedDate = date.toISOString().split('T')[0];
    handleChange('birthDate', formattedDate);
    setDisplayDate(formatToUSDate(formattedDate));
    setShowDatePicker(false);
    onBlur?.();
  };

  const handleManualDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDisplayDate(value);
  };

  const handleManualDateBlur = () => {
    // Try to parse the display date and convert to ISO format
    if (displayDate) {
      const isoDate = parseUSDate(displayDate);
      if (isoDate) {
        handleChange('birthDate', isoDate);
        setDisplayDate(formatToUSDate(isoDate));
      }
    } else {
      handleChange('birthDate', '');
    }
    onBlur?.();
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setShowDatePicker(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const selectedDate = childInfo.birthDate
    ? new Date(childInfo.birthDate + 'T00:00:00')
    : new Date();

  return (
    <div className="checkout-child-info">
      <div className="checkout-participant-header">
        {canRemove && (
          <button
            type="button"
            className="checkout-participant-remove"
            onClick={onRemove}
            aria-label="Remove participant"
          >
            <Trash2 className="icon icon--sm" />
            Remove
          </button>
        )}
      </div>

      <div className="checkout-form-row">
        <div className="form-group checkout-form-group-half">
          <label htmlFor={`child-name-${index}`}>Child's Full Name</label>
          <input
            type="text"
            id={`child-name-${index}`}
            className={`form-input ${errors.fullName ? 'form-input--error' : ''}`}
            value={childInfo.fullName}
            onChange={(e) => handleChange('fullName', e.target.value)}
            onBlur={onBlur}
            placeholder="Full Name"
            required
          />
          {errors.fullName && (
            <span className="form-error">{errors.fullName}</span>
          )}
        </div>

        <div className="form-group checkout-form-group-half">
          <label htmlFor={`child-birthdate-${index}`}>Child's Birth Date</label>
          <div className="date-picker-container" ref={pickerRef}>
            <div className="form-input-with-icon">
              <input
                type="text"
                id={`child-birthdate-${index}`}
                className={`form-input ${errors.birthDate ? 'form-input--error' : ''}`}
                value={displayDate}
                onChange={handleManualDateChange}
                onBlur={handleManualDateBlur}
                onFocus={() => setShowDatePicker(true)}
                placeholder="MM/DD/YYYY"
              />
              <button
                type="button"
                className="form-input-calendar-btn"
                onClick={() => setShowDatePicker(!showDatePicker)}
                aria-label="Open calendar"
              >
                <CalendarIcon className="icon icon--sm" />
              </button>
            </div>
            {showDatePicker && (
              <div className="date-picker-dropdown">
                <Calendar
                  date={selectedDate}
                  onChange={handleDateChange}
                  maxDate={new Date()}
                  color="#ff8a8a"
                />
              </div>
            )}
          </div>
          {errors.birthDate && (
            <span className="form-error">{errors.birthDate}</span>
          )}
        </div>
      </div>

      <div className="form-group">
        <label htmlFor={`medical-conditions-${index}`}>Known allergies, medical conditions or special needs</label>
        <textarea
          id={`medical-conditions-${index}`}
          className="form-input checkout-textarea"
          value={childInfo.medicalConditions}
          onChange={(e) => handleChange('medicalConditions', e.target.value)}
          onBlur={onBlur}
          placeholder="List any allergies, medical conditions, or special requirements (e.g., 'Peanut allergy', 'Requires EpiPen', 'Asthma')"
          rows={3}
        />
      </div>
    </div>
  );
};

export default CheckoutChildInfo;
